import { NgClass } from '@angular/common';
import {
  Component,
  effect,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastItem } from 'src/app/common/toast/toast.component';
import { CurrentUser } from 'src/app/interfaces/current-user';
import { Region } from 'src/app/interfaces/location';
import { User } from 'src/app/interfaces/user';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { ModalsService } from 'src/app/services/Modals/modals.service';
import { ReferenceService } from 'src/app/services/Reference/reference.service';
import { ToastService } from 'src/app/services/Toast/toast.service';
import { UserService } from 'src/app/services/User/user.service';
import { ButtonComponent } from '../../../../common/button/button.component';
import { PageTitleComponent } from '../../../../common/page-title/page-title.component';
import { SpinnerComponent } from '../../../../common/spinner/spinner.component';
import { LocationComponent } from '../common/location/location.component';
import { OtherInformationComponent } from '../common/other-information/other-information.component';
import { PersonalInformationComponent } from '../common/personal-information/personal-information.component';
import { ProfileImageUploadComponent } from '../common/profile-image-upload/profile-image-upload.component';

@Component({
  selector: 'customer-profile',
  imports: [
    PageTitleComponent,
    PersonalInformationComponent,
    ReactiveFormsModule,
    LocationComponent,
    OtherInformationComponent,
    SpinnerComponent,
    ButtonComponent,
    ProfileImageUploadComponent,
    NgClass,
  ],
  templateUrl: './customer.component.html',
  styleUrl: './customer.component.scss',
})
export class CustomerComponent {
  //
  upload_modal = viewChild<ProfileImageUploadComponent>('upload_modal');
  user: WritableSignal<User> = signal({} as User);
  //DATA
  personal_information_form!: FormGroup;
  location_form!: FormGroup;
  other_information_form!: FormGroup;
  data_regions: WritableSignal<Region[]> = signal([]);
  //UI
  editing: boolean = false;
  loading: boolean = true;

  constructor(
    private fb: FormBuilder,
    private userApi: UserService,
    private auth: AuthService,
    private referenceApi: ReferenceService,
    private toast: ToastService,
    private modal: ModalsService
  ) {
    effect(() => {
      if (this.user()) {
        this.loading = false;
        this.apiFetchLocations();
        this.initPersonalInformationForm();
        this.initLocationForm();
        this.initOtherInformationForm();
      }
    });
  }

  ngOnInit() {
    this.fetchUser();
  }

  fetchUser() {
    this.userApi.viewProfile().subscribe({
      next: (val) => this.loadUser(val),
    });
  }
  loadUser(user: User) {
    this.user.set(user);
    const currentUser = this.auth.getCurrentUser() ?? ({} as CurrentUser);

    this.auth.setUser({
      ...currentUser,
      first_name: user.first_name,
      middle_name: user.middle_name,
      last_name: user.last_name,
      image: user.image,
    });
  }
  apiFetchLocations() {
    this.referenceApi.locations().subscribe({
      next: (res) => this.data_regions.set(res),
    });
  }

  initPersonalInformationForm() {
    this.personal_information_form = this.fb.group({
      first_name: [
        this.user().first_name,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-ZÑñ ]*$'),
        ],
      ],
      middle_name: [
        this.user().middle_name ?? '',
        [Validators.maxLength(50), Validators.pattern('^[a-zA-ZÑñ ]*$')],
      ],
      last_name: [
        this.user().last_name,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-ZÑñ ]*$'),
        ],
      ],
      suffix: [this.user().suffix ?? '', [Validators.maxLength(10)]],
      mobile: [
        this.user().mobile,
        [
          Validators.required,
          Validators.pattern('^\\+639[0-9]{9}$'),
          Validators.maxLength(13),
        ],
      ],
    });
  }

  initLocationForm() {
    this.location_form = this.fb.group({
      region_id: [
        this.user().client_details?.region?.uuid ?? '',
        Validators.required,
      ],
      province_id: [
        this.user().client_details?.province?.uuid ?? '',
        Validators.required,
      ],
      city_id: [
        this.user().client_details?.city?.uuid ?? '',
        Validators.required,
      ],
      address: [
        this.user().client_details?.address ?? '',
        [Validators.required, Validators.maxLength(100)],
      ],
    });
  }

  initOtherInformationForm() {
    this.other_information_form = this.fb.group({
      company_id: [
        this.user().client_details?.company.uuid,
        [Validators.required],
      ],
      company_university: [this.user().category, [Validators.maxLength(100)]],
      position: [
        this.user().client_details?.position ?? '',
        [
          Validators.required,
          Validators.pattern('^[a-zA-ZÑñ ]*$'),
          Validators.maxLength(100),
        ],
      ],
      fae_id: [this.user().client_details?.fae.uuid ?? '', []],
      category: [this.user().category, Validators.required],
      sector: [this.user().client_details?.sector ?? '', Validators.required],
    });
    this.other_information_form.disable();
  }

  validForms(): boolean {
    if (
      this.personal_information_form.invalid ||
      this.location_form.invalid ||
      this.other_information_form.invalid
    ) {
      this.toast.makeToast(
        'error',
        'Please complete all required fields',
        5000
      );
      return false;
    }
    return true;
  }

  preventDefaults(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onSuccess(toast: ToastItem, message: string) {
    this.fetchUser();
    this.toast.removeToast(toast);
    this.toast.makeToast('success', message, 5000);
    this.editing = false;
  }

  onError(toast: ToastItem, message: string) {
    this.toast.removeToast(toast);
    this.toast.makeToast('error', message, 5000);
  }

  confirmRemovePhoto(event: Event) {
    this.preventDefaults(event);
    this.modal
      .getInstance()
      .showConfirm(
        'Remove Profile Photo',
        'Are you sure you want to remove your profile photo?',
        'Confirm',
        'Cancel',
        () => this.removePhoto()
      );
  }

  removePhoto() {
    const loading = this.toast.makeStaticToast(
      'loading',
      'Removing profile photo'
    );
    this.userApi.removeProfileImage().subscribe({
      error: () => this.onError(loading, 'Failed to remove profile photo'),
      complete: () => {
        this.fetchUser();
        this.toast.removeToast(loading);
      },
    });
  }

  updateProfileImage(file: File) {
    const loading = this.toast.makeStaticToast(
      'loading',
      'Updating profile photo'
    );
    this.userApi.uploadProfileImage(file).subscribe({
      next: () => this.onSuccess(loading, 'Profile photo updated successfully'),
      error: () => this.onError(loading, 'Failed to update profile photo'),
    });
  }

  save() {
    if (!this.validForms()) return;

    const loading = this.toast.makeStaticToast(
      'loading',
      'Updating profile information'
    );
    const userData: User = {
      ...this.personal_information_form.value,
      ...this.location_form.value,
      ...this.other_information_form.value,
    };

    this.userApi.updateProfile(userData).subscribe({
      next: () => this.onSuccess(loading, 'Profile updated successfully'),
      error: () => this.onError(loading, 'Failed to update profile'),
    });
  }
}
